import type React from 'react';
import { useContext, useEffect, useState } from 'react';
import { Form, Toast, useToast } from '@zillow/constellation-10';
import { DatarouterError, SpinnerContainer } from '@zg-rentals/rental-manager-env';
import { convertAvailabilityDatesToSetAvailabilityLiteDto, type TimeZoneIds } from '@zg-rentals/touring';

import { DISPLAY_SERVER_ERROR_AT } from '../../../constants/WizardConstants';
import { ModularListingContext } from '../../../containers/CreateListingPage/context';
import type { PageWizardProps } from '../../Wizard/PageWizard';
import PageWizard from '../../Wizard/PageWizard';
import WizardAlert from '../../WizardAlert';
import TourSchedulingAvailabilityBody from './components/TourSchedulingAvailabilityBody';
import { useTourSchedulingContext } from './context';
import { WizardContext } from '../../Wizard/context';

import { WizardFlexContainer } from '../../../styles/wizard';

export type TourSchedulingAvailabilityProps = {
  onGoToPrevStep: () => void;
  onGoToNextStep: () => void;
  onExitWizard: () => void;
  wizardTitle: React.ReactNode;
  progressStepper: React.ReactNode;
  totalSteps: number;
  currentStepIndex: number;
  showDesktopViewBreakpoint: string;
} & PageWizardProps;

const TourSchedulingAvailability = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: TourSchedulingAvailabilityProps) => {
  const {
    // @ts-expect-error WizardContext
    wizardState: { isFetchingData },
  } = useContext(WizardContext);
  const {
    modularListingState: { serverErrorObj },
  } = useContext(ModularListingContext);
  const { enqueueToast } = useToast();

  const [clickedNext, setClickedNext] = useState(false);
  const [clickedBackNext, setClickedBack] = useState(false);

  const { availabilityDates, setAvailabilityDates, selectedTimeZone, tourCalendarApiHook } = useTourSchedulingContext();

  useEffect(() => {
    const { fieldName } = serverErrorObj;

    if (!fieldName && !isFetchingData) {
      checkNavToStep();
    }
  }, [isFetchingData]);

  const checkNavToStep = () => {
    if (clickedNext) {
      onNextClick();
    }

    if (clickedBackNext) {
      onBackClick();
    }
  };

  const onNextClick = async () => {
    if (isFetchingData) {
      setClickedNext(true);
      return;
    }

    if (availabilityDates.length > 0) {
      try {
        const setAvailabilityLiteDto = convertAvailabilityDatesToSetAvailabilityLiteDto(availabilityDates);
        await tourCalendarApiHook.setAvailabilitiesLite(setAvailabilityLiteDto);
      } catch (error) {
        const errorMessage = DatarouterError.getDisplayMessage(error);
        return enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
      }
    }
    onGoToNextStep();
  };

  const onBackClick = () => {
    if (isFetchingData) {
      setClickedBack(true);
      return;
    }
    onGoToPrevStep();
  };

  return (
    <PageWizard
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onNextClick}
      onBackClick={onBackClick}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      {...rest}
    >
      <div data-testid="TourSchedulingAvailability">
        {tourCalendarApiHook.isGetTourConfigAndAvailabilityLoading ? (
          <SpinnerContainer />
        ) : (
          <>
            <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.PAGE} />

            <WizardFlexContainer>
              <Form asChild onSubmit={(e: React.FormEvent<HTMLFormElement>) => e.preventDefault()}>
                <TourSchedulingAvailabilityBody
                  availabilityDates={availabilityDates}
                  onAvailabilityDatesChange={setAvailabilityDates}
                  timeZone={selectedTimeZone as TimeZoneIds}
                />
              </Form>
            </WizardFlexContainer>
          </>
        )}
      </div>
    </PageWizard>
  );
};

export default TourSchedulingAvailability;
